import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import type {
  ClientConfiguration as ClientConfigurationType,
  SectionKey,
  SectionMode,
} from "../../../../../types/clientConfig";
import { getInsightsLensLabel } from "../../../../../utils/insights-lens";
import { clientConfigRoleLabels } from "../../../../athlete-portal/profile/components/ClientConfiguration/i18n";
import { useVisibleSections } from "../../../../athlete-portal/profile/components/ClientConfiguration/useVisibleSections";
import { SECTION_RADIO_CONFIG } from "../../../../athlete-portal/profile/components/ClientConfiguration/utils";

function labelForSectionMode(
  t: (key: string, vars?: Record<string, string>) => string,
  key: SectionKey,
  mode: SectionMode | undefined,
  roleLabels: Record<string, string>
) {
  const block = SECTION_RADIO_CONFIG.find((c) => c.key === key);
  const opt = block?.options.find((o) => o.value === mode);
  return opt ? t(opt.labelKey, roleLabels) : "—";
}

export const ClientConfigurationTemplateCard = ({
  template,
  referAsClients = false,
}: {
  template: ClientConfigurationType;
  referAsClients?: boolean;
}) => {
  const { t } = useTranslation(["athlete-portal", "dashboard"]);
  const roleLabels = clientConfigRoleLabels(t, referAsClients);
  const {
    isTrainingVisible,
    isGoalsVisible,
    isYourPlansVisible,
    activeTrackerCount,
  } = useVisibleSections({ clientConfig: template });

  const sections = template.sections;

  const summaryRows = useMemo(
    () => [
      {
        label: t("clientConfiguration.sections.training"),
        value: isTrainingVisible
          ? t("clientConfiguration.templateCard.on")
          : t("clientConfiguration.templateCard.off"),
        emoji: "🏋",
      },
      {
        label: t("clientConfiguration.sections.nutrition"),
        value: labelForSectionMode(
          t,
          "nutrition",
          sections?.nutrition?.mode,
          roleLabels
        ),
        emoji: "🥗",
      },
      {
        label: t("clientConfiguration.sections.mealPlans"),
        value: labelForSectionMode(
          t,
          "mealPlans",
          sections?.mealPlans?.mode,
          roleLabels
        ),
        emoji: "📋",
      },
      {
        label: t("clientConfiguration.sections.dailyAccountabilitiesShort"),
        value: labelForSectionMode(
          t,
          "dailyAccountabilities",
          sections?.dailyAccountabilities?.mode,
          roleLabels
        ),
        emoji: "✅",
      },
      {
        label: t("clientConfiguration.sections.goals"),
        value: isGoalsVisible
          ? t("clientConfiguration.templateCard.on")
          : t("clientConfiguration.templateCard.off"),
        emoji: "🎯",
      },
      {
        label: t("clientConfiguration.sections.trackingRequirements"),
        value: t("clientConfiguration.templateCard.activeCount", {
          count: activeTrackerCount,
        }),
        emoji: "📊",
      },
      {
        label: t("clientConfiguration.sections.yourPlans"),
        value: isYourPlansVisible
          ? t("clientConfiguration.templateCard.on")
          : t("clientConfiguration.templateCard.off"),
        emoji: "📅",
      },
      ...(template.defaultInsightsLens
        ? [
            {
              label: t("clientConfiguration.templateCard.insightsLens"),
              value: getInsightsLensLabel(t, template.defaultInsightsLens),
              emoji: "📈",
            },
          ]
        : []),
    ],
    [
      template.defaultInsightsLens,
      sections?.nutrition?.mode,
      sections?.mealPlans?.mode,
      sections?.dailyAccountabilities?.mode,
      isTrainingVisible,
      isGoalsVisible,
      isYourPlansVisible,
      activeTrackerCount,
      roleLabels,
      t,
    ]
  );

  const subtitle = t("clientConfiguration.templateCard.tracker", {
    count: activeTrackerCount,
  });

  const openEdit = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        "modal[type]": "add-client-configuration-template",
        currentConfigTemplateId: template._id,
        tabView: "client-configuration-tab",
        templateView: "client-configuration",
      },
    });
  };

  const openView = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        "modal[type]": "client-configuration-templates",
        tabView: "client-configuration-tab",
        templateView: "client-configuration",
      },
    });
  };

  const removeTemplate = () => {
    if (
      !globalThis.confirm(t("clientConfiguration.templateCard.deleteConfirm"))
    ) {
      return;
    }
    submit("/actions/client-configuration/delete-template", {
      method: "POST",
      body: { templateId: template._id },
    });
  };

  console.log("franco summary rows --> ", JSON.stringify(summaryRows, null, 4));

  return (
    <div className="check-in-template-card client-configuration-template-card">
      <div className="check-in-template-card__header">
        <h3 className="check-in-template-card__title">
          {template.templateName?.trim() ||
            t("clientConfiguration.templateCard.untitled")}
        </h3>
        <span className="check-in-template-card__count">{subtitle}</span>
      </div>

      <div className="check-in-template-card__questions client-configuration-template-card__body">
        <div className="check-in-template-card__questions-scroll client-configuration-template-card__scroll">
          {summaryRows.map((row) => (
            <div
              key={row.label}
              className="client-configuration-template-card__row"
            >
              <span className="client-configuration-template-card__label">
                {row.emoji} {row.label}
              </span>
              <span className="client-configuration-template-card__value">
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="check-in-template-card__footer">
        <button
          type="button"
          className="check-in-template-card__edit-btn"
          onClick={openEdit}
        >
          <img src="/img/new-icons/edit-pencil.svg" alt="" />
          {t("clientConfiguration.templateCard.editTemplate")}
        </button>
        <div className="check-in-template-card__actions">
          <button
            type="button"
            data-button-type="secondary"
            data-icon-only
            onClick={openView}
          >
            <img
              src="/img/new-icons/eye.svg"
              alt={t("clientConfiguration.templateCard.viewTemplatesAlt")}
              className="w-5 h-5"
            />
          </button>
          <button
            type="button"
            data-button-type="secondary"
            data-icon-only
            onClick={removeTemplate}
          >
            <img
              src="/img/new-icons/red-trash.svg"
              alt={t("clientConfiguration.templateCard.deleteTemplateAlt")}
              className="w-5 h-5 filter-none!"
            />
          </button>
        </div>
      </div>
    </div>
  );
};
